// ─────────────────────────────────────────────────────────────────────────────
// The pseudo-programs of the context toolbar's « Programme » menu (#815, widened by #948).
//
// Each stands for a FAMILY of programs rather than one. They are plain `LearningProgram`
// objects whose id is a well-known GUID, so they flow through everything a real program id
// flows through — persistence, hash params, offline cache keys, program-scoped API routes —
// with no special casing: the server recognizes the three ids on its read endpoints and turns
// them into a filter (see ProgramScope.cs).
//
// #948 is why there are three. The menu used to render a « Individualisés » heading that was
// not clickable, with a « Tous les individualisés » line right under it — two lines saying the
// same thing, one of them dead — and nothing at all for the group programs. Now each heading IS
// the entry, and a third says « all of them ».
//
// `ALL_INDIVIDUAL` keeps Guid.Empty, the value #815 shipped: it sits in persisted preferences,
// in URL hash parameters and in offline cache keys, so changing it would silently reset every
// saved context.
// ─────────────────────────────────────────────────────────────────────────────
import type { LearningProgram } from '@/sdk/model/learning-program';

/** Every individualized program at once. */
export const PROGRAM_SCOPE_ALL_INDIVIDUAL = '00000000-0000-0000-0000-000000000000';
/** Every group program at once. */
export const PROGRAM_SCOPE_ALL_GROUP = '00000000-0000-0000-0000-000000000001';
/** No constraint on the program at all. */
export const PROGRAM_SCOPE_ALL = '00000000-0000-0000-0000-000000000002';

export type ProgramScopeKind = 'individual' | 'group' | 'all';

const SCOPE_BY_ID: Record<string, ProgramScopeKind> = {
  [PROGRAM_SCOPE_ALL_INDIVIDUAL]: 'individual',
  [PROGRAM_SCOPE_ALL_GROUP]: 'group',
  [PROGRAM_SCOPE_ALL]: 'all',
};

function idOf(program: { id: string } | string | null | undefined): string | null {
  if (!program) return null;
  return typeof program === 'string' ? program : program.id;
}

/** Which family the entry stands for, or null for a real program. */
export function programScopeKind(
  program: { id: string } | string | null | undefined
): ProgramScopeKind | null {
  const id = idOf(program);
  return id ? SCOPE_BY_ID[id] ?? null : null;
}

/** True for any of the three pseudo-entries (or its bare id). */
export function isProgramScope(program: { id: string } | string | null | undefined): boolean {
  return programScopeKind(program) !== null;
}

/**
 * True for « Individualisés » specifically. Kept as its own predicate because several screens
 * ask exactly this question — an individualized-only view widened to the union — and reading
 * `programScopeKind(p) === 'individual'` at each of them would invite the other two kinds to be
 * forgotten silently.
 */
export function isIndividualizedScope(
  program: { id: string } | string | null | undefined
): boolean {
  return programScopeKind(program) === 'individual';
}

/**
 * `isIndividual` of the family, for callers that speak in that flag: true, false, or undefined
 * when the entry constrains nothing (« Tous les programmes ») or is a real program.
 */
export function programScopeFamily(
  program: { id: string } | string | null | undefined
): boolean | undefined {
  const kind = programScopeKind(program);
  if (kind === 'individual') return true;
  if (kind === 'group') return false;
  return undefined;
}

function scopeEntry(id: string, title: string, isIndividual: boolean): LearningProgram {
  return {
    id,
    title,
    code: '',
    archived: false,
    isAvailable: true,
    isIndividual,
    workStudy: false,
    numberOfHours: 0,
    scheduleReady: true,
    language: '',
    // `sites: []` on purpose: an empty set contributes no pin constraint (see the store's
    // `intersectionOf`), and the site filter special-cases the entries instead — a family of
    // programs exists wherever its members do.
    sites: [],
  };
}

/**
 * The three menu entries. `isIndividual` is set so the toolbar files each under the right
 * heading — which, for these three, is the heading itself.
 *
 * « Tous les programmes » is filed as a group program only because the menu has two sides and it
 * has to sit on one; the toolbar lifts it out of the sections and renders it first, above them
 * both (see context-toolbar.vue).
 */
export function buildProgramScopeEntries(): LearningProgram[] {
  return [
    scopeEntry(PROGRAM_SCOPE_ALL, 'Tous les programmes', false),
    scopeEntry(PROGRAM_SCOPE_ALL_GROUP, 'Groupes', false),
    scopeEntry(PROGRAM_SCOPE_ALL_INDIVIDUAL, 'Individualisés', true),
  ];
}

export interface ProgramScopeOptions {
  /** Offer « Individualisés » — only when there is a real individualized program to unite. */
  individual: boolean;
  /** Offer « Groupes » — same rule on the other side. */
  group: boolean;
}

/**
 * Which scopes a list of programs can carry. A family with no member has nothing to unite, and
 * « Tous les programmes » needs at least one program of any kind.
 */
export function availableProgramScopes(programs: readonly LearningProgram[]): ProgramScopeOptions {
  const real = programs.filter(p => !isProgramScope(p));
  return {
    individual: real.some(p => p.isIndividual),
    group: real.some(p => !p.isIndividual),
  };
}

/**
 * Prepends the pseudo-entries to a freshly loaded program list, keeping only those that have
 * something to unite. Prepended, not appended: the menu partitions by kind while preserving
 * order, so each entry leads its section whatever the server's sort.
 */
export function withProgramScopeEntries(
  programs: LearningProgram[],
  allowed: ProgramScopeOptions = availableProgramScopes(programs),
): LearningProgram[] {
  const real = programs.filter(p => !isProgramScope(p));
  const kept = buildProgramScopeEntries().filter(entry => {
    const kind = programScopeKind(entry);
    if (kind === 'individual') return allowed.individual;
    if (kind === 'group') return allowed.group;
    // « Tous les programmes » only means something when both families are on offer; with one,
    // it would be a second name for that one.
    return allowed.individual && allowed.group;
  });
  return [...kept, ...real];
}
